/**
 * AI Creator Page
 *
 * Full-page React component for AI presentation creation.
 * Streamlined flow: prompt -> generating -> auto-compile -> redirect to editor.
 * Images generate in the background in the editor, not here.
 */

import React, { useState, useCallback, useEffect, useRef } from 'react';
import { AIProvider, useAI } from './context/AIContext';
import { AIGenerationOptions, AICreditsInfo, aiService } from './services/AIService';
import { PresentationContent } from '../../ai/schemas/ContentBlock';
import { FlowCompiler } from '../../ai/compiler/FlowCompiler';
import { insertTOCScreen } from '../../ai/tocGenerator';
import { wrapFlowInDocument, serializeDocument } from '../../ai/compiler/flowExporter';
import { getThemeById } from '../../ai/styles/themeLibrary';
import CreditPacksModal from './components/CreditPacksModal';
import AuthGateModal from './components/AuthGateModal';
import { RecentPromptsSheet } from './components/RecentPrompts';
import { VIEW_SIZES } from '../../ai/schemas/LayoutTemplate';
import { AssemblingFlowLoading, Screen as AsmScreen } from './loading/AssemblingFlowLoading';
import { extractLiveStreamState, LiveStreamState } from './utils/streamParser';
import { ThemeChrome, LiveSlidePreview, getThemeColors } from './components/LiveSlidePreview';
import { TypingContent } from './utils/streamParser';

/**
 * Elapsed seconds counter - ticks once per second while mounted
 */
function ElapsedSeconds({ className }: { className?: string }) {
  const [seconds, setSeconds] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setSeconds(s => s + 1), 1000);
    return () => clearInterval(id);
  }, []);
  return <span className={className || 'ai-elapsed-seconds'}>{seconds}s</span>;
}

/**
 * Page state machine (simplified: no more 'preview' step)
 */
type PageState = 'prompt' | 'generating';

/**
 * Generation options for the form
 */
interface FormOptions {
  screenCount: number;
  // Empty string = "Default (let AI choose)" — not sent in the payload.
  // Otherwise one of the explicit tone presets.
  tone: AIGenerationOptions['tone'] | '';
  audience: string;
  // Content density. Defaults to 'concise' (Flow2's mobile-first default).
  contentStyle: 'concise' | 'detailed';
  aspectRatio: 'widescreen' | 'standard';
  linkUrl: string;
  // Empty string = use server default (Claude). Otherwise an OpenRouter
  // model id from window.flowboardConfig.openrouterModels.
  model: string;
  // How to handle an uploaded .pptx/.pdf. 'convert' (default) runs the
  // deterministic deck-import converter (keeps the original layout/images);
  // 'reimagine' runs the AI upload path that re-authors it. Only meaningful
  // when the selected file is a deck; ignored for other file types.
  importMode?: 'convert' | 'reimagine';
}

/** True when a file is a deck the faithful converter accepts (.pptx / .pdf). */
function isDeckFile(file: File | null | undefined): boolean {
  return !!file && /\.(pptx|pdf)$/i.test(file.name);
}

/**
 * AI Creator Page wrapper with provider
 */
export function AICreatorPage() {
  return (
    <AIProvider>
      <AICreatorPageContent />
    </AIProvider>
  );
}

/**
 * Demo mode for viewing all theme chromes
 */
const DEMO_THEMES = ['launch', 'editorial', 'impact', 'refined', 'terminal', 'warm', 'swiss', 'phosphor', 'coral-sunset', 'minimal'];

function ThemeChromeDemo() {
  const demoContent: TypingContent = {
    title: 'Sample Title',
    subtitle: 'A compelling subtitle',
    bodyLines: ['This is body text that demonstrates the theme.'],
    bulletItems: ['First bullet point', 'Second bullet point'],
    screenType: 'content',
    isTypingTitle: false,
    isTypingSubtitle: false,
    isTypingBody: false,
    isTypingBullets: false,
  };

  return (
    <div className="ai-creator-page">
      <main className="ai-creator-main" style={{ padding: '24px' }}>
        <h1 style={{ marginBottom: '24px', color: '#fff' }}>Theme Chrome Gallery</h1>
        <p style={{ marginBottom: '32px', color: '#888' }}>Preview all 10 theme frames. Visit /ai/create to return to normal mode.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '32px' }}>
          {DEMO_THEMES.map(themeId => (
            <div key={themeId} style={{ textAlign: 'center' }}>
              <h3 style={{ marginBottom: '12px', color: '#fff', textTransform: 'capitalize' }}>{themeId}</h3>
              <ThemeChrome themeId={themeId}>
                <LiveSlidePreview content={demoContent} themeId={themeId} themeColors={getThemeColors(themeId)} />
              </ThemeChrome>
            </div>
          ))}
        </div>
      </main>
    </div>
  );
}

/**
 * AI Creator Page content
 */
function AICreatorPageContent() {
  // Check for demo mode
  const urlParams = new URLSearchParams(window.location.search);
  const demoMode = urlParams.get('demo');

  if (demoMode === 'themes') {
    return <ThemeChromeDemo />;
  }

  const [pageState, setPageState] = useState<PageState>('prompt');
  const [selectedAspectRatio, setSelectedAspectRatio] = useState<'widescreen' | 'standard'>('widescreen');
  // Survives PromptForm unmount/remount so the user's prompt comes back into the
  // textfield after a generation error instead of forcing them to retype.
  const [lastPrompt, setLastPrompt] = useState<string>('');

  // Streaming state
  const [streamingText, setStreamingText] = useState('');
  const [liveStreamState, setLiveStreamState] = useState<LiveStreamState | null>(null);
  const [expectedScreenCount, setExpectedScreenCount] = useState(5);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [streamError, setStreamError] = useState<string | null>(null);
  const streamControllerRef = useRef<AbortController | null>(null);

  // File upload state
  const [isFileUpload, setIsFileUpload] = useState(false);
  const [uploadFileName, setUploadFileName] = useState('');
  const [uploadStage, setUploadStage] = useState<string>('received');
  // 'ai' = AI reimagine upload; 'deck' = faithful deck-import converter.
  // Drives the progress-overlay copy so a faithful convert doesn't say
  // "AI is analyzing".
  const [importVariant, setImportVariant] = useState<'ai' | 'deck'>('ai');

  // Captured at submit-time so the V5 loading screen's elapsed counter starts
  // from the actual generation kickoff, not from when liveStreamState first
  // becomes non-null (~0.5-2s later).
  const [submitTimeMs, setSubmitTimeMs] = useState<number>(0);

  // Auth gate modal state — for anonymous users who try to Generate.
  // pendingSubmitRef holds the args of the original submit so we can replay
  // it after the user authenticates inside the modal (avoids stashing in
  // sessionStorage + page reload). authGateReason swaps the modal copy —
  // file uploads always require an account (even with anonGenerationEnabled)
  // so the "we need a signed-in account to upload" copy is clearer than
  // the generic "we've saved your prompt" one.
  const [showAuthGate, setShowAuthGate] = useState(false);
  const [authGateReason, setAuthGateReason] = useState<'generate' | 'upload'>('generate');
  const pendingSubmitRef = useRef<{ prompt: string; options: FormOptions; file?: File } | null>(null);

  // Credits state
  const [credits, setCredits] = useState<AICreditsInfo | null>(null);
  // Anon users have no credits state — skip the fetch (it would 401 and
  // fall back to balance:0, which trips the "Not Enough Credits" gate on
  // a button anon users should see as "Generate Flow"). Mark loading=false
  // immediately so the gate condition `!creditsLoading && credits && ...`
  // stays false (credits remains null).
  const isAnonOnMount = !((window as any).flowboardConfig?.isLoggedIn);
  const [creditsLoading, setCreditsLoading] = useState(!isAnonOnMount);

  useEffect(() => {
    if (isAnonOnMount) return;
    const fetchCredits = async () => {
      setCreditsLoading(true);
      const info = await aiService.getCredits();
      setCredits(info);
      setCreditsLoading(false);
    };
    fetchCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const {
    presentation,
    selectedTheme,
    setSelectedTheme,
    sessionId,
    setPresentation,
    setSessionId,
    generate,
    error,
    reset,
  } = useAI();

  // Elapsed time counter during generation
  useEffect(() => {
    if (pageState !== 'generating') {
      setElapsedSeconds(0);
      return;
    }
    const timer = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => clearInterval(timer);
  }, [pageState]);

  // Telemetry: planning phase shown (generating but no streaming state yet).
  const planningStartRef = useRef<number | null>(null);
  useEffect(() => {
    const ob = (window as any).flow2Onboarding;
    if (pageState === 'generating' && !liveStreamState) {
      planningStartRef.current = Date.now();
      if (ob && typeof ob.capture === 'function') ob.capture('flow_planning_shown');
    } else if (pageState === 'generating' && liveStreamState && planningStartRef.current) {
      // Planning ended — streaming has begun.
      const dur = Date.now() - planningStartRef.current;
      planningStartRef.current = null;
      if (ob && typeof ob.capture === 'function') ob.capture('flow_planning_duration_ms', { duration_ms: dur });
    }
  }, [pageState, liveStreamState]);

  /**
   * Fire the onboarding bumps that used to live in the client compile+export
   * effect, then redirect to the mobile editor. The async worker path already
   * compiled + persisted the flow, so all we have left to do client-side is
   * the analytics bookkeeping + navigation.
   */
  const redirectToEditor = useCallback((stringId: string, totalScreens: number) => {
    const ob = (window as any).flow2Onboarding;
    // Bump generation count before redirect so the editor sees the right flow_number.
    if (ob && typeof ob.incrementBuildAmbient === 'function') ob.incrementBuildAmbient();
    // Stash flow id so the build_view_completed event has it; commonProps reads window.flowStringId.
    (window as any).flowStringId = stringId;
    if (ob && typeof ob.capture === 'function') {
      ob.capture('flow_build_view_completed', { total_screens: totalScreens || 0 });
    }
    window.location.href = `/m/${stringId}/edit`;
  }, []);

  /**
   * Faithful deck import (.pptx / .pdf → structure-preserving flow). Runs the
   * deterministic converter via /ai/import-deck instead of the AI upload path.
   * Drives the same FileUploadOverlay progress UI (no SSE stream), then
   * redirects to the editor on completion.
   */
  const runDeckImport = useCallback((file: File) => {
    setLastPrompt('');
    setSelectedAspectRatio('widescreen');
    setPageState('generating');
    setStreamError(null);
    setLiveStreamState(null);
    setIsFileUpload(true);
    setUploadFileName(file.name);
    setUploadStage('received');
    setImportVariant('deck');
    setSubmitTimeMs(Date.now());

    const ob = (window as any).flow2Onboarding;
    if (ob && typeof ob.capture === 'function') {
      ob.capture('flow_generation_started', { is_file_upload: true, import_mode: 'convert' });
    }

    const controller = aiService.importDeck(
      file,
      // onProgress — map the worker's step string onto the overlay's stages.
      (step) => {
        const s = (step || '').toLowerCase();
        if (s.includes('polish') || s.includes('creating') || s.includes('flow')) {
          setUploadStage('generating');
        } else {
          setUploadStage('extracting');
        }
      },
      // onComplete — worker already compiled + persisted; just redirect.
      (result) => {
        if (result.stringId) {
          redirectToEditor(result.stringId, result.screens || 0);
        } else if (result.editUrl) {
          window.location.href = result.editUrl;
        } else {
          setStreamError('Import finished but no flow was returned. Check My Flows.');
          setPageState('prompt');
        }
      },
      // onError
      (errorMsg) => {
        setStreamError(errorMsg === 'insufficient_credits' ? 'insufficient_credits' : errorMsg);
        if (errorMsg === 'insufficient_credits') {
          aiService.getCredits().then(info => setCredits(info));
        }
        setPageState('prompt');
      }
    );
    streamControllerRef.current = controller;
  }, [redirectToEditor]);

  /**
   * Reconnect/recovery: resolve an in-flight create job by polling job-status.
   * complete → redirect; processing/pending → re-open the relay SSE;
   * failed/not-found → clear marker + return to the prompt.
   */
  const recoverJob = useCallback((jobId: string, attempt: number = 0, screenCountOverride?: number) => {
    // Screen count for the recovery preview. Threaded explicitly because the
    // mount recovery path fires before any setExpectedScreenCount re-render
    // has updated the closure (the original count is stored in sessionStorage
    // at enqueue and restored here). Falls back to state for the relay-drop
    // recovery path, which runs after a submit already set the state.
    const recScreens = (typeof screenCountOverride === 'number' && screenCountOverride > 0)
      ? screenCountOverride
      : expectedScreenCount;
    // Bound the recovery loop. A hard worker crash (fatal/parse error) used to
    // leave the api_jobs row stuck in 'processing' forever — the relay would
    // relay_timeout, re-poll, see 'processing', reopen the relay, time out
    // again, with no terminal error ever shown. The worker now has a shutdown
    // guard that marks crashed jobs 'failed', but this client cap is the
    // belt-and-suspenders: after MAX_RECOVERY_ATTEMPTS relay/poll cycles with no
    // terminal state, give up and surface a retryable error instead of spinning.
    const clearJobMarker = () => {
      try {
        sessionStorage.removeItem('flow2_create_job');
        sessionStorage.removeItem('flow2_create_job_screens');
      } catch { /* ignore */ }
    };
    const MAX_RECOVERY_ATTEMPTS = 8;
    if (attempt > MAX_RECOVERY_ATTEMPTS) {
      clearJobMarker();
      setStreamError('Generation is taking longer than expected. Please try again.');
      setPageState('prompt');
      return;
    }
    const poll = async () => {
      const status = await aiService.getJobStatus(jobId);
      if (!status) {
        // Network blip — try again shortly (counts toward the attempt cap).
        setTimeout(() => recoverJob(jobId, attempt + 1, recScreens), 1500);
        return;
      }
      if (status.status === 'complete') {
        const polledSid = status.flow_id || status.string_id;
        if (polledSid) {
          setPageState('generating');
          // CRITICAL for anon creators: this recovery channel (job-status poll)
          // is reached when the relay SSE dropped (tab backgrounded mid-gen —
          // the incident scenario). The relay's `complete` branch is what binds
          // the new flow to the browser session (_registerAnonFlow) for the
          // editor edit-gate; on this path it never ran. Bind via the server
          // BEFORE redirecting, or the anon user is denied editing their own
          // flow. Harmless no-op for signed-in users.
          const boundSid = await aiService.bindJobFlow(jobId);
          clearJobMarker();
          redirectToEditor(boundSid || polledSid, recScreens);
        } else {
          clearJobMarker();
          setPageState('prompt');
        }
        return;
      }
      if (status.status === 'failed') {
        clearJobMarker();
        setStreamError(status.error || 'Generation failed');
        setPageState('prompt');
        return;
      }
      if (status.status === 'pending' || status.status === 'processing') {
        // Still running — show the generating screen and re-open the relay for
        // the live preview. If the relay drops again, this same recovery fires.
        setPageState('generating');
        const controller = new AbortController();
        streamControllerRef.current = controller;
        aiService.streamJob(
          jobId,
          (_chunk, fullText) => {
            setStreamingText(fullText);
            setLiveStreamState(extractLiveStreamState(fullText, recScreens));
          },
          (result) => {
            if (result.success && result.stringId) {
              clearJobMarker();
              redirectToEditor(result.stringId, recScreens);
            } else {
              setStreamError(result.error || 'Generation failed');
              setPageState('prompt');
            }
          },
          (errorMsg) => {
            // Relay dropped again — fall back to a status poll. Increment the
            // attempt so a never-terminating job can't loop forever (the cap at
            // the top of recoverJob then surfaces a retryable error).
            if (errorMsg === 'stream_incomplete' || errorMsg === 'relay_timeout') {
              setTimeout(() => recoverJob(jobId, attempt + 1, recScreens), 1000);
            } else {
              setStreamError(errorMsg);
              setPageState('prompt');
            }
          },
          controller.signal
        );
        return;
      }
      // Unknown / not-found.
      clearJobMarker();
      setPageState('prompt');
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expectedScreenCount, redirectToEditor]);

  // On mount: if a create job was in flight (tab backgrounded mid-generation,
  // page reloaded, etc.), recover it. This is the actual disconnect-recovery
  // the incident wanted — the flow is saved server-side, so we just resolve it.
  const recoverOnceRef = useRef(false);
  useEffect(() => {
    if (recoverOnceRef.current) return;
    recoverOnceRef.current = true;
    let jobId: string | null = null;
    let storedScreens: string | null = null;
    try {
      jobId = sessionStorage.getItem('flow2_create_job');
      storedScreens = sessionStorage.getItem('flow2_create_job_screens');
    } catch { jobId = null; }
    if (jobId) {
      // Seed the expected screen count from the original request so the
      // recovery relay preview renders the right number of building screens
      // (recoverJob feeds extractLiveStreamState with expectedScreenCount).
      const parsed = storedScreens ? parseInt(storedScreens, 10) : NaN;
      const recoverScreens = Number.isFinite(parsed) && parsed > 0 ? parsed : expectedScreenCount;
      setExpectedScreenCount(recoverScreens);
      recoverJob(jobId, 0, recoverScreens);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handle prompt submission with streaming
   */
  const handlePromptSubmit = useCallback(
    async (prompt: string, options: FormOptions, file?: File) => {
      // Anonymous gate — never fire a real AI call without an account,
      // UNLESS the anonymous-flow-creation kill switch is on (then anon
      // visitors submit directly and the resulting flow gets attributed
      // to the shared anon_user account on the server). See
      // docs/ANON_USER_CREATING.md. The AuthGateModal path still runs when
      // anonGenerationEnabled is false so we have a safe fallback if we
      // need to flip the switch at runtime.
      const isLoggedIn = !!(window as any).flowboardConfig?.isLoggedIn;
      const anonGenerationEnabled = !!(window as any).flowboardConfig?.anonGenerationEnabled;
      if (!isLoggedIn && !anonGenerationEnabled) {
        pendingSubmitRef.current = { prompt, options, file };
        setAuthGateReason(file ? 'upload' : 'generate');
        setShowAuthGate(true);
        return;
      }
      if (!isLoggedIn && anonGenerationEnabled && file) {
        // File uploads stay gated even with the anon kill switch on. The
        // multipart File is held in pendingSubmitRef across the modal's
        // auth round-trip, so once the user authenticates we replay the
        // same submit with the same File — no re-pick required.
        pendingSubmitRef.current = { prompt, options, file };
        setAuthGateReason('upload');
        setShowAuthGate(true);
        return;
      }

      // Faithful deck import: a .pptx/.pdf runs the deterministic converter by
      // default (keeps the original design). The AI-reimagine path only runs
      // when the user explicitly chose 'reimagine' in the upload tile. Branch
      // here — before the AI-path telemetry + state setup — so deck import
      // owns its own progress + analytics.
      if (file && isDeckFile(file) && options.importMode !== 'reimagine') {
        runDeckImport(file);
        return;
      }
      // AI upload / text path from here down.
      setImportVariant('ai');
      // Stash the prompt so it survives any error path back to 'prompt'.
      setLastPrompt(prompt);
      // Telemetry: prompt submission. Send length only (no PII).
      const ob = (window as any).flow2Onboarding;
      if (ob && typeof ob.capture === 'function') {
        ob.capture('flow_generation_started', {
          prompt_length: (prompt || '').length,
          screen_count: options.screenCount,
          tone: options.tone,
          audience: options.audience,
          content_style: options.contentStyle,
          is_file_upload: !!file
        });
      }
      setSelectedAspectRatio(options.aspectRatio);
      setExpectedScreenCount(options.screenCount);
      setPageState('generating');
      setStreamingText('');
      setLiveStreamState(null);
      setStreamError(null);
      setIsFileUpload(!!file);
      setUploadFileName(file?.name || '');
      setUploadStage('received');
      setSubmitTimeMs(Date.now());

      // Only ship tone + audience when the user explicitly set them. Empty values
      // mean "no preference" — let the AI pick. Don't bake a default into the prompt.
      const genOptions: AIGenerationOptions = {
        slideCount: options.screenCount,
        linkUrl: options.linkUrl,
        model: options.model || undefined,
      };
      if (options.tone) genOptions.tone = options.tone;
      if (options.audience.trim()) genOptions.audience = options.audience.trim();
      // Content style always ships (it has a real default, unlike tone/audience).
      genOptions.contentStyle = options.contentStyle;

      // For file uploads, use streaming upload endpoint
      if (file) {
        const controller = aiService.uploadStream(
          file,
          genOptions,
          prompt || undefined,
          // onStatus - update upload stage
          (stage, message) => {
            console.log('[uploadStream] status:', stage, message);
            setUploadStage(stage);
          },
          // onChunk - update streaming state with live parsing
          (chunk, fullText) => {
            setStreamingText(fullText);
            const state = extractLiveStreamState(fullText, options.screenCount);
            setLiveStreamState(state);
          },
          // onComplete
          (result) => {
            if (result.success && result.presentation) {
              setPresentation(result.presentation);
              if (result.sessionId) {
                setSessionId(result.sessionId);
              }
              setLiveStreamState(prev => prev ? { ...prev, isComplete: true } : null);
              if (result.creditsRemaining !== undefined) {
                setCredits(prev => prev ? { ...prev, balance: result.creditsRemaining! } : prev);
                sessionStorage.setItem('ai_credits_used', JSON.stringify({
                  used: result.creditsUsed || 10,
                  remaining: result.creditsRemaining,
                  timestamp: Date.now()
                }));
              }
            } else {
              if (result.error === 'insufficient_credits') {
                setStreamError('insufficient_credits');
                aiService.getCredits().then(info => setCredits(info));
              } else {
                setStreamError(result.error || 'Generation failed');
              }
              setPageState('prompt');
            }
          },
          // onError
          (errorMsg) => {
            setStreamError(errorMsg);
            setPageState('prompt');
          }
        );
        streamControllerRef.current = controller;
        return;
      }

      // Disconnect-proof create for text prompts: enqueue a server-side job
      // (the worker generates AND persists), then open the SSE relay for the
      // live typing preview. The flow is saved server-side regardless of
      // whether this tab survives — fixing the mid-generation data-loss bug.
      const controller = aiService.createAsync(
        prompt,
        { ...genOptions, aspectRatio: options.aspectRatio },
        // onChunk - update streaming state with live parsing (unchanged)
        (_chunk, fullText) => {
          setStreamingText(fullText);
          const state = extractLiveStreamState(fullText, options.screenCount);
          setLiveStreamState(state);
        },
        // onComplete - the worker already compiled + persisted; just redirect.
        (result) => {
          if (result.success && result.stringId) {
            setLiveStreamState(prev => prev ? { ...prev, isComplete: true } : null);
            // No longer in flight — clear the reconnect markers.
            try {
              sessionStorage.removeItem('flow2_create_job');
              sessionStorage.removeItem('flow2_create_job_screens');
            } catch { /* ignore */ }
            redirectToEditor(result.stringId, expectedScreenCount);
          } else {
            if (result.error === 'insufficient_credits') {
              setStreamError('insufficient_credits');
              aiService.getCredits().then(info => setCredits(info));
            } else {
              setStreamError(result.error || 'Generation failed');
            }
            setPageState('prompt');
          }
        },
        // onError
        (errorMsg) => {
          // Soft signals from the relay (stream dropped / timed out) are NOT
          // terminal — the worker may still be saving. Recover via job-status.
          if (errorMsg === 'stream_incomplete' || errorMsg === 'relay_timeout') {
            const jobId = (() => { try { return sessionStorage.getItem('flow2_create_job'); } catch { return null; } })();
            if (jobId) {
              recoverJob(jobId);
              return;
            }
          }
          if (errorMsg === 'insufficient_credits') {
            setStreamError('insufficient_credits');
            aiService.getCredits().then(info => setCredits(info));
          } else {
            setStreamError(errorMsg);
          }
          setPageState('prompt');
        },
        // onEnqueued - persist the job id so a tab backgrounding can recover.
        // Also persist the requested screen count: a recovered job re-opens the
        // relay and feeds extractLiveStreamState, which needs the expected count
        // to render the right number of building screens. Without this the
        // recovery preview would use the default (5) regardless of the original
        // request (display-only cosmetic mismatch; the saved flow is unaffected).
        (jobId) => {
          try {
            sessionStorage.setItem('flow2_create_job', jobId);
            sessionStorage.setItem('flow2_create_job_screens', String(options.screenCount));
          } catch { /* ignore */ }
        }
      );

      streamControllerRef.current = controller;
    },
    [setPresentation, setSessionId, credits, expectedScreenCount, redirectToEditor, recoverJob, runDeckImport]
  );

  // Cancel streaming on unmount
  useEffect(() => {
    return () => {
      streamControllerRef.current?.abort();
    };
  }, []);

  /**
   * Auto-compile and redirect when a `presentation` arrives.
   *
   * NOTE (2026-06-23, Web Create → Job migration): the TEXT-prompt path no
   * longer sets `presentation` — it routes through aiService.createAsync (the
   * disconnect-proof worker job) and redirects directly from onComplete, so
   * this effect is a no-op for text creates. It remains the path for the
   * FILE-UPLOAD create flow (aiService.uploadStream still streams client-side
   * and sets `presentation` on complete). The file-upload create is explicitly
   * DEFERRED from the job migration (see docs/WEB_CREATE_JOB_MIGRATION.md), so
   * it keeps the legacy client-compile + /ai/export path here. Do not wire the
   * text path back into this effect.
   * - Desktop: Store to sessionStorage and redirect to /ai/edit
   * - Mobile: Export to Treemo and redirect to /m/{id}/edit for inline editing
   */
  useEffect(() => {
    if (!presentation || pageState !== 'generating') return;

    const isMobile = (window as any).flowboardConfig?.isMobile || window.innerWidth <= 768;

    const handleGeneration = async () => {
      try {
        // Auto-select AI-suggested theme (check top-level first, then globalMetadata)
        const pres = presentation as PresentationContent;
        const suggestedId = (pres as any).suggestedThemeId || pres.globalMetadata?.suggestedThemeId;
        const directHit = suggestedId ? getThemeById(suggestedId) : undefined;

        // File-themes (noir, impact, dispatch, editorial, etc.) live in include/themes/*.json
        // and aren't registered in the React-side PREDEFINED_THEMES Map. Before this shim
        // the editor silently fell back to selectedTheme (clean/minimal default), so the
        // /m/ player never saw the AI's actual choice. Build a shim that borrows the
        // fallback's compile-time fields but stamps the correct id — flowExporter writes
        // theme.id into content.themeId, and the player's theme sweep takes it from there.
        const filePalettes = (window as any).flowboardConfig?.themePalettes as
          | Record<string, { name?: string }>
          | undefined;
        const isFileTheme = !!(suggestedId && !directHit && filePalettes && filePalettes[suggestedId]);
        // Brand-Import themes (user-{id}-{slug}) are persisted to include/themes/
        // and applied by the backend AI-gen default-theme fallback. They're not
        // in the React PREDEFINED_THEMES Map and may not appear in themePalettes
        // either — pass the id through so flowExporter writes the right
        // content.themeId. The /m/ player resolves the JSON via theme_loader.
        const isUserTheme = !!(suggestedId && !directHit && !isFileTheme && /^user-\d+-/.test(suggestedId));
        const theme = directHit
          || (isFileTheme
            ? { ...selectedTheme, id: suggestedId!, name: filePalettes![suggestedId!].name || suggestedId! }
            : isUserTheme
              ? { ...selectedTheme, id: suggestedId!, name: suggestedId! }
              : selectedTheme);

        if (directHit) {
          setSelectedTheme(directHit);
        }

        // Determine view size based on selected aspect ratio
        const viewSize = selectedAspectRatio === 'widescreen'
          ? VIEW_SIZES.WIDESCREEN
          : VIEW_SIZES.STANDARD;

        const pContent = presentation as PresentationContent;

        // Insert TOC screen after title slide (if enough screens)
        const screensWithTOC = insertTOCScreen([...pContent.screens]);
        const presentationWithTOC: PresentationContent = {
          ...pContent,
          screens: screensWithTOC,
        };

        // Debug: Log presentation content before compilation
        console.log('[AICreatorPage] Presentation screens before compile:', presentationWithTOC.screens.length);
        console.log('[AICreatorPage] TOC inserted:', screensWithTOC.length > pContent.screens.length);
        console.log('[AICreatorPage] Using view size:', viewSize, 'for aspect ratio:', selectedAspectRatio);
        console.log('[AICreatorPage] isMobile:', isMobile);

        // Compile with placeholders (no resolved images yet — those generate in editor)
        const compiler = new FlowCompiler({
          presentation: presentationWithTOC,
          theme,
          viewSize,
        });
        const compilationResult = compiler.compile();

        // Debug: Log compiled flow screen count
        console.log('[AICreatorPage] Compiled flow screens:', compilationResult.flow.screens.length,
          compilationResult.flow.screens.map((s, i) => ({
            index: i,
            id: s.id?.substring(0, 8),
            mmoCount: s.mixedMediaObjects?.length || 0
          }))
        );

        // Wrap with content layer + sessionId
        const document = wrapFlowInDocument(compilationResult.flow, {
          title: presentation.title,
          theme,
          presentation: presentationWithTOC,
          layoutIds: compilationResult.layoutIds,
          contentMapping: compilationResult.contentMapping,
          sessionId: sessionId || undefined,
        });

        const flowJson = JSON.parse(serializeDocument(document));

        // Debug: Verify final document structure
        console.log('[AICreatorPage] Final document screens:', flowJson.flowboard.flows[0]?.screens?.length);

        // Always export to Treemo and redirect to mobile editor
        console.log('[AICreatorPage] Exporting to Treemo...');

        const exportResponse = await fetch('/ai/export', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            flow_json: flowJson,
            title: presentation.title,
          }),
        });

        const exportResult = await exportResponse.json();

        if (exportResult.success && exportResult.string_id) {
          console.log('[AICreatorPage] Flow exported, redirecting to /m/' + exportResult.string_id + '/edit');
          // Onboarding: bump generation count before the redirect so the editor sees the right flow_number.
          const ob = (window as any).flow2Onboarding;
          if (ob && typeof ob.incrementBuildAmbient === 'function') ob.incrementBuildAmbient();
          // Stash flow id so the build_view_completed event has it; commonProps reads window.flowStringId.
          (window as any).flowStringId = exportResult.string_id;
          if (ob && typeof ob.capture === 'function') {
            ob.capture('flow_build_view_completed', {
              total_screens: presentation?.screens?.length || 0
            });
          }
          window.location.href = `/m/${exportResult.string_id}/edit`;
        } else {
          console.error('[AICreatorPage] Export failed:', exportResult.error);
          setPageState('prompt');
        }
      } catch (err) {
        console.error('Auto-compile failed:', err);
        setPageState('prompt');
      }
    };

    handleGeneration();
  }, [presentation, pageState, selectedAspectRatio]); // eslint-disable-line react-hooks/exhaustive-deps

  // Check if mobile
  const isMobile = (window as any).flowboardConfig?.isMobile || window.innerWidth <= 768;

  return (
    <div className="ai-creator-page">
      {/* Header removed - using side_logged_in_nav from PHP instead */}

      <main className="ai-creator-main">
        {pageState === 'prompt' && (
          <PromptForm
            onSubmit={handlePromptSubmit}
            error={error || streamError}
            credits={credits}
            creditsLoading={creditsLoading}
            initialPrompt={lastPrompt}
          />
        )}

        {pageState === 'generating' && liveStreamState && (() => {
          const completed = liveStreamState.completedScreens;
          const typing = liveStreamState.currentScreen;
          const expected = Math.max(
            liveStreamState.totalExpectedScreens,
            completed.length + (typing ? 1 : 0)
          );

          // Pull body + bullets from a completed screen's blocks.
          // Bullets stay as a separate array so the active card can render
          // them as a proper <ul> (per user feedback: "if it's bulleted text,
          // show them as bullets"). Falls back to a joined preview string for
          // the strip-row overlay (which is single-line anyway).
          const bodyOf = (s: typeof completed[number]): { body?: string; bullets?: string[] } => {
            const blocks = Array.isArray(s.blocks) ? s.blocks : [];
            const bodyTexts: string[] = [];
            let bullets: string[] | undefined;
            for (const b of blocks) {
              if (!b || typeof b !== 'object') continue;
              if (b.type === 'title' || b.type === 'subtitle') continue;
              if (b.type === 'bullet-list' && Array.isArray(b.items)) {
                bullets = b.items.filter((it: any) => typeof it === 'string' && it.trim());
                continue;
              }
              if (typeof b.text === 'string' && b.text.trim()) {
                bodyTexts.push(b.text);
              }
            }
            return {
              body: bodyTexts.join(' ').trim() || undefined,
              bullets,
            };
          };

          const screens: AsmScreen[] = [
            ...completed.map((s, i): AsmScreen => {
              const { body, bullets } = bodyOf(s);
              return {
                n: i + 1,
                title: s.title || `Screen ${i + 1}`,
                body,
                bullets,
                status: 'done',
              };
            }),
            ...(typing
              ? [{
                  n: completed.length + 1,
                  // Empty title (no "Screen X" placeholder) — ActiveCard
                  // renders a typing-caret line until the real title arrives.
                  title: typing.title,
                  body: typing.bodyLines.join(' ') || typing.subtitle || '',
                  bullets: typing.bulletItems && typing.bulletItems.length > 0 ? typing.bulletItems : undefined,
                  status: 'current' as const,
                }]
              : []),
            ...Array.from(
              { length: Math.max(0, expected - completed.length - (typing ? 1 : 0)) },
              (_, i): AsmScreen => ({
                n: completed.length + (typing ? 1 : 0) + i + 1,
                // Empty title for pending screens — strips render just the
                // number when title is empty (no more "Screen X" flash).
                title: '',
                status: 'pending',
              })
            ),
          ];

          const currentIndex = completed.length;
          const truncatedPrompt = lastPrompt && lastPrompt.length > 60
            ? lastPrompt.slice(0, 57) + '…'
            : lastPrompt;
          const flowTitle = liveStreamState.presentationTitle || truncatedPrompt || 'Your flow';

          return (
            <AssemblingFlowLoading
              screens={screens}
              currentIndex={currentIndex}
              flowTitle={flowTitle}
              themeId={liveStreamState.suggestedThemeId || null}
              inputMode={isFileUpload ? 'file' : 'prompt'}
              startTimeMs={submitTimeMs || Date.now()}
              isFinishing={liveStreamState.isComplete}
            />
          );
        })()}

        {pageState === 'generating' && !liveStreamState && (
          isFileUpload ? (
            <FileUploadOverlay fileName={uploadFileName} stage={uploadStage} variant={importVariant} />
          ) : (
            <div className="ai-generating-container">
              <div className="ai-generating-card">
                <div className="ai-generating-spinner">
                  <svg viewBox="0 0 50 50" width="60" height="60">
                    <circle
                      cx="25"
                      cy="25"
                      r="20"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="4"
                      strokeLinecap="round"
                      strokeDasharray="80 40"
                    />
                  </svg>
                </div>
                <p>Analyzing and planning…</p>
              </div>
            </div>
          )
        )}
      </main>

      <AuthGateModal
        isOpen={showAuthGate}
        onClose={() => setShowAuthGate(false)}
        reason={authGateReason}
        onAuthed={() => {
          setShowAuthGate(false);
          const pending = pendingSubmitRef.current;
          pendingSubmitRef.current = null;
          if (pending) {
            // Replay the original submit. The auth cookie is now set, so the
            // gate at the top of handlePromptSubmit will pass through to the
            // real generation path.
            handlePromptSubmit(pending.prompt, pending.options, pending.file);
          }
        }}
      />
    </div>
  );
}

/**
 * Prompt Form Component with inline optional refinement questions
 */
function PromptForm({
  onSubmit,
  error,
  credits,
  creditsLoading,
  initialPrompt = '',
}: {
  onSubmit: (prompt: string, options: FormOptions, file?: File) => void;
  error: string | null;
  credits: AICreditsInfo | null;
  creditsLoading: boolean;
  initialPrompt?: string;
}) {
  // Seed from initialPrompt so a generation error returning the user to this form
  // restores what they typed instead of dropping them on an empty field.
  const [prompt, setPrompt] = useState(initialPrompt);
  // If the user came back with a non-empty prompt, default-open the Describe tile
  // so they can immediately see and edit their text.
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  // For a .pptx/.pdf: 'convert' (faithful deck import, default) or 'reimagine'
  // (AI re-authors). Only surfaced/used when a deck file is selected.
  const [deckMode, setDeckMode] = useState<'convert' | 'reimagine'>('convert');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showBuyCredits, setShowBuyCredits] = useState(false);
  // Error modal dismissal — error stays in context but the modal closes on
  // user dismiss. Re-opens automatically on any NEW error (effect below
  // resets the flag whenever `error` changes).
  const [errorDismissed, setErrorDismissed] = useState(false);
  useEffect(() => { setErrorDismissed(false); }, [error]);
  const [options, setOptions] = useState<FormOptions>({
    screenCount: 5,
    // '' = Default (let AI choose). Don't ship a tone preset unless the user picked one.
    tone: '',
    audience: '',
    contentStyle: 'concise',
    aspectRatio: 'widescreen',
    linkUrl: '',
    model: '',
  });

  // Inline refinement fields
  const [goal, setGoal] = useState('');
  const [keyPoints, setKeyPoints] = useState('');
  const [cta, setCta] = useState('');

  // Tile mode: 'describe' (textarea + example chips) or 'upload' (file picker).
  // Default to 'describe' so the textarea is visible on load — users shouldn't
  // have to click to discover where to type.
  const [inputMode, setInputMode] = useState<'describe' | 'upload' | null>('describe');
  const [showGuideOptions, setShowGuideOptions] = useState(false);
  // Recent prompts browser (docs/CREATE_RECENTS_WEB_SPEC.md). Logged-in only —
  // /ai/my-generations 401s for anonymous sessions.
  const isLoggedIn = !!((window as any).flowboardConfig?.isLoggedIn);
  const [showRecents, setShowRecents] = useState(false);
  const handleUseRecentPrompt = useCallback((text: string) => {
    setPrompt(text);
    setInputMode('describe');
    setShowRecents(false);
    // Defer so the describe tile (and its textarea) is mounted before focusing.
    setTimeout(() => {
      const ta = document.getElementById('prompt') as HTMLTextAreaElement | null;
      if (ta) {
        ta.focus();
        ta.setSelectionRange(ta.value.length, ta.value.length);
      }
    }, 60);
  }, []);
  // Screen-count UI: free users get slider 1-10, Pro users get slider 1-25
  // with "Custom" reveal that unlocks 26-100. Free users see an upgrade nudge
  // inside the reveal. Server-side clamp in ai.php::_clampSlideCountForUser
  // is the source of truth — this is UX, not enforcement.
  const isPro = !!((window as any).flowboardConfig?.isPro);
  const SLIDER_MAX = isPro ? 25 : 10;
  const [showCustomScreens, setShowCustomScreens] = useState(false);
  const SCREEN_HARD_CAP = 100;
  // Draft string for the Custom number input. Lets users type multi-digit values
  // freely without the field clamping on every keystroke; we clamp on blur.
  const [customScreensDraft, setCustomScreensDraft] = useState<string>('26');
  useEffect(() => {
    if (showCustomScreens) {
      setCustomScreensDraft(options.screenCount > 25 ? String(options.screenCount) : '26');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showCustomScreens]);

  // Hydrate from sessionStorage `flow2_pending` once on mount. This is the
  // post-login resume path: anonymous users who hit Generate get their prompt
  // stashed + bounced through /user/login → /user → referring_page cookie →
  // back here, where we hydrate and auto-fire the submit. Set
  // `flow2_pending_handled` so the static-form IIFE in create.php doesn't
  // also try to click submit.
  useEffect(() => {
    try {
      const pending = sessionStorage.getItem('flow2_pending');
      if (!pending) return;
      const data = JSON.parse(pending);
      if (!data || !data.prompt) return;
      sessionStorage.removeItem('flow2_pending');
      sessionStorage.setItem('flow2_pending_handled', '1');
      setPrompt(data.prompt);
      setInputMode('describe');
      const hydratedOptions: FormOptions = {
        screenCount: parseInt(data.slide_count, 10) || 5,
        tone: data.tone || '',
        audience: data.audience || '',
        contentStyle: data.content_style === 'detailed' ? 'detailed' : 'concise',
        aspectRatio: 'widescreen',
        linkUrl: data.link_url || '',
        model: data.model || '',
      };
      setOptions(hydratedOptions);
      if (data.autosubmit) {
        // Defer one tick so React commits the state update (so an error path
        // returning to PromptForm sees the populated form).
        setTimeout(() => {
          onSubmit(data.prompt, hydratedOptions);
          sessionStorage.removeItem('flow2_pending_handled');
        }, 50);
      } else {
        sessionStorage.removeItem('flow2_pending_handled');
      }
    } catch (e) {
      console.error('[PromptForm] flow2_pending hydrate failed:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ALLOWED_EXTENSIONS = ['pdf', 'pptx', 'txt'];

  const validateFile = (file: File): string | null => {
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      return 'Unsupported file type. Please upload a PDF, PPTX, or TXT file.';
    }
    // Decks route through the faithful converter (backend cap 100MB); other
    // file types stay on the lighter AI text-extraction path (50MB).
    const maxMb = isDeckFile(file) ? 100 : 50;
    if (file.size > maxMb * 1024 * 1024) {
      return `File too large. Maximum size is ${maxMb}MB.`;
    }
    return null;
  };

  const handleFileSelect = (file: File) => {
    setFileError(null);
    const err = validateFile(file);
    if (err) {
      setFileError(err);
      return;
    }
    setSelectedFile(file);
    setInputMode('upload');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('dragover');
    if (e.dataTransfer.files.length > 0) {
      handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  // Cycling placeholder examples — rotate every 3.2s while the textarea is empty
  // and unfocused. Replaces the static placeholder so users see what the field accepts.
  const PROMPT_PLACEHOLDERS = [
    'A pitch deck for my SaaS idea…',
    'Paste a full outline or notes here for sharper screens…',
    'A course outline for intro to data analytics…',
    'A product demo for our analytics tool…',
    'Drop your meeting notes and we’ll structure them…',
    'An onboarding flow for new hires…',
    'A 5-screen brand story for our launch…',
  ];
  const [placeholderIdx, setPlaceholderIdx] = useState(0);
  useEffect(() => {
    if (prompt || inputMode !== 'describe') return;
    const t = setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % PROMPT_PLACEHOLDERS.length);
    }, 3200);
    return () => clearInterval(t);
  }, [prompt, inputMode, PROMPT_PLACEHOLDERS.length]);

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!prompt.trim() && !selectedFile) return;

    // Build enhanced prompt with optional answers
    let enhancedPrompt = prompt;
    const details: string[] = [];

    if (goal) {
      details.push(`Main goal: ${goal}`);
    }
    if (keyPoints.trim()) {
      details.push(`Key points to emphasize: ${keyPoints.trim()}`);
    }
    if (cta) {
      details.push(`Desired audience action: ${cta}`);
    }

    if (details.length > 0) {
      enhancedPrompt += '\n\nAdditional details:\n' + details.join('\n');
    }

    // Carry the deck-import choice only when a deck file is selected; the
    // parent ignores importMode for non-deck files.
    const submitOptions = isDeckFile(selectedFile)
      ? { ...options, importMode: deckMode }
      : options;
    onSubmit(enhancedPrompt, submitOptions, selectedFile || undefined);
  };

  return (
    <div className="ai-prompt-container">
      <div className="ai-prompt-card">
        <h1 className="ai-prompt-title">Start Your Flow</h1>
        <p className="ai-prompt-subtitle">
          Describe it or drop a file. AI drafts it, you shape it.
        </p>

        <form onSubmit={handleSubmit} className="ai-prompt-form">
          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.pptx,.txt"
            style={{ display: 'none' }}
            onChange={(e) => {
              if (e.target.files?.[0]) handleFileSelect(e.target.files[0]);
            }}
          />

          {/* Equal-weight Describe / Upload tiles. Tap to expand inline; the
              other tile dims. State persists across mode switches. */}
          <div className="ai-input-tiles">
            <div
              className={`ai-input-tile ai-input-tile--describe ${
                inputMode === 'describe' ? 'expanded' : ''
              } ${inputMode && inputMode !== 'describe' ? 'dimmed' : ''}`}
            >
              <button
                type="button"
                className="ai-input-tile-header"
                aria-expanded={inputMode === 'describe'}
                onClick={() => setInputMode('describe')}
              >
                <span className="ai-input-tile-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 20h9" />
                    <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
                  </svg>
                </span>
                <span className="ai-input-tile-text">
                  <span className="ai-input-tile-title">Describe it</span>
                  <span className="ai-input-tile-hint">Type your idea, or paste a full outline. More detail makes better screens.</span>
                </span>
              </button>
              {isLoggedIn && (
                <button
                  type="button"
                  className="ai-recents-chip"
                  onClick={() => setShowRecents(true)}
                >
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <polyline points="1 4 1 10 7 10" />
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
                    <polyline points="12 7 12 12 15 14" />
                  </svg>
                  Recent
                </button>
              )}
              {inputMode === 'describe' && (
                <div className="ai-input-tile-content">
                  <textarea
                    id="prompt"
                    autoFocus
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    placeholder={
                      selectedFile
                        ? 'Focus on key findings, use a professional tone…'
                        : PROMPT_PLACEHOLDERS[placeholderIdx]
                    }
                    rows={3}
                    required={!selectedFile}
                  />
                </div>
              )}
            </div>

            <div className="ai-input-tiles-divider" aria-hidden="true">
              <span>or</span>
            </div>

            <div
              className={`ai-input-tile ai-input-tile--upload ${
                inputMode === 'upload' ? 'expanded' : ''
              } ${inputMode && inputMode !== 'upload' ? 'dimmed' : ''}`}
              onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('dragover'); }}
              onDragLeave={(e) => e.currentTarget.classList.remove('dragover')}
              onDrop={handleDrop}
            >
              <button
                type="button"
                className="ai-input-tile-header"
                aria-expanded={inputMode === 'upload'}
                onClick={() => {
                  setInputMode('upload');
                  if (!selectedFile) fileInputRef.current?.click();
                }}
              >
                <span className="ai-input-tile-icon">
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="17 8 12 3 7 8" />
                    <line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </span>
                <span className="ai-input-tile-text">
                  <span className="ai-input-tile-title">Upload a file</span>
                  <span className="ai-input-tile-hint">PDF, PPT, TXT</span>
                </span>
              </button>
              {inputMode === 'upload' && (
                <div className="ai-input-tile-content">
                  {selectedFile ? (
                    <>
                    <div className="ai-upload-file-info">
                      <span className="file-details">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                          <polyline points="14 2 14 8 20 8" />
                        </svg>
                        <span>{selectedFile.name}</span>
                        <span className="file-size">{formatFileSize(selectedFile.size)}</span>
                      </span>
                      <button
                        type="button"
                        className="file-remove"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileError(null);
                          if (fileInputRef.current) fileInputRef.current.value = '';
                        }}
                        title="Remove file"
                      >
                        &times;
                      </button>
                    </div>
                    {isDeckFile(selectedFile) && (
                      <div className="ai-deck-mode" role="radiogroup" aria-label="How to import this deck">
                        <button
                          type="button"
                          role="radio"
                          aria-checked={deckMode === 'convert'}
                          className={`ai-deck-mode-option ${deckMode === 'convert' ? 'selected' : ''}`}
                          onClick={() => setDeckMode('convert')}
                        >
                          <span className="ai-deck-mode-title">Keep original design</span>
                          <span className="ai-deck-mode-hint">Convert your slides as-is, keeping layout and images</span>
                        </button>
                        <button
                          type="button"
                          role="radio"
                          aria-checked={deckMode === 'reimagine'}
                          className={`ai-deck-mode-option ${deckMode === 'reimagine' ? 'selected' : ''}`}
                          onClick={() => setDeckMode('reimagine')}
                        >
                          <span className="ai-deck-mode-title">Reimagine with AI</span>
                          <span className="ai-deck-mode-hint">Let AI rebuild it into a fresh mobile flow</span>
                        </button>
                      </div>
                    )}
                    </>
                  ) : (
                    <button
                      type="button"
                      className="ai-upload-rechoose"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Choose a file or drag it here · max 50 MB
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {fileError && (
            <div className="ai-error" style={{ marginBottom: 12 }}>
              <span className="ai-error-icon">!</span>
              {fileError}
            </div>
          )}

          <div className="ai-advanced-options">
            <div className="ai-form-group">
              <div className="ai-screens-label-row">
                <label htmlFor="screenCount">Number of screens</label>
              </div>
              <div className="ai-screens-value">
                {options.screenCount} {options.screenCount === 1 ? 'screen' : 'screens'}
              </div>
              <input
                id="screenCount"
                type="range"
                min={1}
                max={SLIDER_MAX}
                step={1}
                value={Math.min(options.screenCount, SLIDER_MAX)}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setOptions({ ...options, screenCount: v });
                  if (showCustomScreens) setShowCustomScreens(false);
                }}
                className="ai-screens-slider"
              />
              <div className="ai-screens-scale">
                <span>1</span>
                <span>{SLIDER_MAX}</span>
              </div>
              <button
                type="button"
                className={`ai-screens-custom-toggle ${showCustomScreens ? 'expanded' : ''}`}
                onClick={() => setShowCustomScreens(!showCustomScreens)}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points={showCustomScreens ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
                </svg>
                Custom <span className="ai-screens-custom-hint">(more than {SLIDER_MAX})</span>
              </button>
              {showCustomScreens && (
                isPro ? (
                  <div className="ai-screens-custom-row">
                    <input
                      type="number"
                      min={26}
                      max={SCREEN_HARD_CAP}
                      value={customScreensDraft}
                      onChange={(e) => {
                        // Let the user type freely (including transient values).
                        setCustomScreensDraft(e.target.value);
                        const n = parseInt(e.target.value, 10);
                        if (Number.isFinite(n) && n >= 26 && n <= SCREEN_HARD_CAP) {
                          setOptions({ ...options, screenCount: n });
                        }
                      }}
                      onBlur={(e) => {
                        const n = parseInt(e.target.value, 10);
                        const clamped = Number.isFinite(n) ? Math.max(26, Math.min(SCREEN_HARD_CAP, n)) : 26;
                        setCustomScreensDraft(String(clamped));
                        setOptions({ ...options, screenCount: clamped });
                      }}
                      className="ai-screens-custom-input"
                    />
                    <span className="ai-screens-custom-hint-inline">
                      26 to {SCREEN_HARD_CAP} screens
                    </span>
                  </div>
                ) : (
                  <div className="ai-screens-custom-locked">
                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <span>Upgrade to Pro for {SLIDER_MAX + 1}+ screens</span>
                    <a href="/pricing" className="ai-screens-upgrade-link">Upgrade</a>
                  </div>
                )
              )}
            </div>
            {/* Format selector - DISABLED for now
            {!((window as any).flowboardConfig?.isMobile || window.innerWidth <= 768) && (
              <div className="ai-form-group">
                <label>Format</label>
                <div className="ai-aspect-ratio-options">
                  <button
                    type="button"
                    className={`ai-aspect-option ${options.aspectRatio === 'widescreen' ? 'selected' : ''}`}
                    onClick={() => setOptions({ ...options, aspectRatio: 'widescreen' })}
                  >
                    <div className="ai-aspect-preview ai-aspect-widescreen"></div>
                    <span>Widescreen</span>
                    <small>16:9 · Desktop & Displays</small>
                  </button>
                  <button
                    type="button"
                    className={`ai-aspect-option ${options.aspectRatio === 'standard' ? 'selected' : ''}`}
                    onClick={() => setOptions({ ...options, aspectRatio: 'standard' })}
                  >
                    <div className="ai-aspect-preview ai-aspect-standard"></div>
                    <span>Standard</span>
                    <small>4:3 · iPad & Classic</small>
                  </button>
                </div>
              </div>
            )}
            */}
          </div>

          {/* Content style - Concise (default, mobile-first) vs Detailed.
              See docs/CONCISE_DETAILED_SWITCH_SPEC.md */}
          <div className="ai-form-group">
            <label>Content style</label>
            <div className="ai-aspect-ratio-options">
              <button
                type="button"
                className={`ai-aspect-option ${options.contentStyle === 'concise' ? 'selected' : ''}`}
                onClick={() => setOptions({ ...options, contentStyle: 'concise' })}
              >
                <span>Concise</span>
                <small>Recommended · one idea per screen</small>
              </button>
              <button
                type="button"
                className={`ai-aspect-option ${options.contentStyle === 'detailed' ? 'selected' : ''}`}
                onClick={() => setOptions({ ...options, contentStyle: 'detailed' })}
              >
                <span>Detailed</span>
                <small>More explanation per screen</small>
              </button>
            </div>
          </div>

          {/* Link URL - optional CTA for last screen */}
          <div className="ai-form-group">
            <label htmlFor="linkUrl">
              End with a call to action <span style={{color: '#52525b', fontWeight: 400}}>(optional)</span>
            </label>
            <input
              id="linkUrl"
              type="url"
              value={options.linkUrl}
              onChange={(e) => setOptions({ ...options, linkUrl: e.target.value })}
              placeholder="https://yoursite.com"
            />
            <p style={{fontSize: '11px', color: '#52525b', margin: '4px 0 0'}}>
              Link to any webpage. For example, book a demo, sign up, or learn more.
            </p>
          </div>

          {/* Guide the AI - collapsible */}
          <button
            type="button"
            className={`ai-guide-toggle ${showGuideOptions ? 'expanded' : ''}`}
            onClick={() => setShowGuideOptions(!showGuideOptions)}
          >
            <svg
              className="ai-guide-toggle-icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <polyline points={showGuideOptions ? "18 15 12 9 6 15" : "6 9 12 15 18 9"} />
            </svg>
            Guide the AI
            <span className="ai-guide-toggle-hint">(optional)</span>
          </button>

          <div className={`ai-guide-options ${showGuideOptions ? 'expanded' : ''}`}>
            <div className="ai-form-group">
              <label htmlFor="tone">Tone</label>
              <select
                id="tone"
                value={options.tone}
                onChange={(e) =>
                  setOptions({
                    ...options,
                    tone: e.target.value as FormOptions['tone'],
                  })
                }
              >
                <option value="">Default (let AI choose)</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="formal">Formal</option>
                <option value="creative">Creative</option>
                <option value="educational">Educational</option>
              </select>
            </div>

            <div className="ai-form-group">
              <label htmlFor="audience">Target audience</label>
              <input
                id="audience"
                type="text"
                value={options.audience}
                onChange={(e) => setOptions({ ...options, audience: e.target.value })}
                placeholder="E.g., investors, students, executives..."
              />
            </div>

            <div className="ai-form-group">
              <label>What is the main goal?</label>
              <div className="ai-suggestions">
                {['Inform/Educate', 'Persuade/Sell', 'Inspire/Motivate', 'Report/Update'].map((suggestion) => (
                  <button
                    key={suggestion}
                    type="button"
                    className={`ai-suggestion-chip ${goal === suggestion ? 'selected' : ''}`}
                    onClick={() => setGoal(goal === suggestion ? '' : suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>

            <div className="ai-form-group">
              <label htmlFor="keyPoints">Key points to emphasize?</label>
              <textarea
                id="keyPoints"
                value={keyPoints}
                onChange={(e) => setKeyPoints(e.target.value)}
                placeholder="E.g., market size, competitive advantage, team experience..."
                rows={2}
              />
            </div>

            <div className="ai-form-group">
              <label htmlFor="model">AI model</label>
              <select
                id="model"
                value={options.model}
                onChange={(e) => setOptions({ ...options, model: e.target.value })}
              >
                <option value="">Auto (Claude Sonnet)</option>
                {((window as any).flowboardConfig?.openrouterModels || []).map(
                  (m: { id: string; label: string; pro_only?: boolean }) => {
                    const locked = m.pro_only && !(window as any).flowboardConfig?.isPro;
                    return (
                      <option key={m.id} value={m.id} disabled={locked}>
                        {m.label}{locked ? ' (Pro)' : ''}
                      </option>
                    );
                  }
                )}
              </select>
              <p style={{ fontSize: '11px', color: '#52525b', margin: '4px 0 0' }}>
                Try alternative models. Leave on Auto for the default.
              </p>
            </div>
          </div>

          {error && !errorDismissed && (
            <div
              className="ai-error-modal-overlay"
              role="dialog"
              aria-modal="true"
              aria-labelledby="ai-error-modal-title"
              onClick={() => setErrorDismissed(true)}
            >
              <div
                className={`ai-error-modal ${error === 'insufficient_credits' ? 'is-gentle' : ''}`}
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  type="button"
                  className="ai-error-modal-close"
                  aria-label="Dismiss"
                  onClick={() => setErrorDismissed(true)}
                >
                  ×
                </button>
                <div className="ai-error-modal-icon" aria-hidden="true">
                  {error === 'insufficient_credits' ? '◔' : '!'}
                </div>
                <h2 id="ai-error-modal-title" className="ai-error-modal-title">
                  {error === 'insufficient_credits' ? 'Out of credits' : 'Generation failed'}
                </h2>
                <p className="ai-error-modal-body">
                  {error === 'insufficient_credits' ? (
                    <>You need {credits?.costs?.generation || 10} credits to generate this flow. You have {credits?.balance || 0}.</>
                  ) : (
                    error
                  )}
                </p>
                <div className="ai-error-modal-actions">
                  {error === 'insufficient_credits' ? (
                    <>
                      <button
                        type="button"
                        className="ai-btn ai-btn-primary"
                        onClick={() => {
                          setErrorDismissed(true);
                          setShowBuyCredits(true);
                        }}
                      >
                        Get more credits
                      </button>
                      <button
                        type="button"
                        className="ai-error-modal-secondary"
                        onClick={() => setErrorDismissed(true)}
                      >
                        Maybe later
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      className="ai-btn ai-btn-primary"
                      onClick={() => setErrorDismissed(true)}
                    >
                      OK
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <p className="ai-form-note-inline">
            Flows are made of connected screens. We'll structure them for you.
          </p>

          {/* Subtle credits hint - only show for returning users (who have used AI before) */}
          {!creditsLoading && credits && !credits.is_admin && credits.lifetime_spent > 0 && (
            <div className="ai-credits-hint">
              {credits.costs?.generation || 10} credits · {credits.balance} remaining <span className="ai-buy-credits-link" onClick={() => setShowBuyCredits(true)}>Buy more</span>
            </div>
          )}

          <button
            type="submit"
            className="ai-btn ai-btn-primary ai-btn-full ai-generate-cta"
            disabled={
              (!prompt.trim() && !selectedFile) ||
              (!creditsLoading && credits && !credits.is_admin && credits.balance < (credits.costs?.generation || 10))
            }
          >
            {!creditsLoading && credits && !credits.is_admin && credits.balance < (credits.costs?.generation || 10)
              ? 'Not Enough Credits'
              : 'Generate Flow'}
          </button>
        </form>

      <CreditPacksModal
        isOpen={showBuyCredits}
        onClose={() => setShowBuyCredits(false)}
        onPurchaseComplete={() => {
          setShowBuyCredits(false);
          // Refresh credits
          aiService.getCredits().then(info => {
            // credits is passed as prop, we need parent to refresh
            // For now, just close and let user see updated balance on next action
          });
        }}
        currentBalance={credits?.balance || 0}
      />

      <RecentPromptsSheet
        open={showRecents}
        onClose={() => setShowRecents(false)}
        onUsePrompt={handleUseRecentPrompt}
      />
      </div>
    </div>
  );
}

/**
 * File Upload Overlay Component
 * Shows real-time progress stages from server SSE events
 */
function FileUploadOverlay({ fileName, stage, variant = 'ai' }: { fileName: string; stage: string; variant?: 'ai' | 'deck' }) {
  // Map server stages to display stages. Deck import (faithful convert) gets
  // its own copy — it's structure-preserving, not an AI rewrite, so "AI is
  // analyzing" would be misleading.
  const stages = variant === 'deck'
    ? [
        { id: 'received', label: 'File received' },
        { id: 'extracting', label: 'Converting slides' },
        { id: 'generating', label: 'Building your flow' },
      ]
    : [
        { id: 'received', label: 'File received' },
        { id: 'extracting', label: 'Reading document' },
        { id: 'generating', label: 'AI is analyzing' },
      ];

  const activeStageIndex = stages.findIndex(s => s.id === stage);
  const activeStage = activeStageIndex >= 0 ? activeStageIndex : 0;

  // Get file extension for display
  const ext = fileName.split('.').pop()?.toUpperCase() || 'File';
  const heading = variant === 'deck' ? `Converting ${ext}...` : `Processing ${ext}...`;

  return (
    <div className="ai-generating-container">
      <div className="ai-generating-card">
        <ElapsedSeconds className="ai-elapsed-corner" />
        <div className="ai-generating-spinner">
          <svg viewBox="0 0 50 50" width="60" height="60">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="80 40"
            />
          </svg>
        </div>
        <h2>{heading}</h2>
        <p className="ai-upload-filename">{fileName}</p>
        <div className="ai-generating-stages">
          {stages.map((s, i) => (
            <div
              key={s.id}
              className={`ai-stage ${i <= activeStage ? 'active' : ''} ${i === activeStage ? 'current' : ''}`}
            >
              {s.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Generating State Component
 * Steps through stages on a 3-second timer to give visual progress feedback.
 */
function GeneratingState() {
  const [activeStage, setActiveStage] = useState(0);

  useEffect(() => {
    const d1 = (8 + Math.random() * 3) * 1000;
    const d2 = d1 + (5 + Math.random() * 5) * 1000;
    const d3 = d2 + (5 + Math.random() * 5) * 1000;
    const t1 = setTimeout(() => setActiveStage(1), d1);
    const t2 = setTimeout(() => setActiveStage(2), d2);
    const t3 = setTimeout(() => setActiveStage(3), d3);
    return () => { clearTimeout(t1); clearTimeout(t2); clearTimeout(t3); };
  }, []);

  const stages = ['Analyzing prompt', 'Generating content', 'Initiating image generation', 'Organizing screens'];

  return (
    <div className="ai-generating-container">
      <div className="ai-generating-card">
        <ElapsedSeconds className="ai-elapsed-corner" />
        <div className="ai-generating-spinner">
          <svg viewBox="0 0 50 50" width="60" height="60">
            <circle
              cx="25"
              cy="25"
              r="20"
              fill="none"
              stroke="currentColor"
              strokeWidth="4"
              strokeLinecap="round"
              strokeDasharray="80 40"
            />
          </svg>
        </div>
        <h2>Creating your presentation...</h2>
        <p>AI is generating content for your screens</p>
        <div className="ai-generating-stages">
          {stages.map((label, i) => (
            <div
              key={label}
              className={`ai-stage ${i <= activeStage ? 'active' : ''} ${i === activeStage ? 'current' : ''}`}
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default AICreatorPage;
